import { first, orderBy } from 'lodash-es';
import { ClinicalItem, FileWithTimestamp } from '@features/components/ClinicalData/types';
import {
  formatClinicalTime,
  getBloodSamplesRadiometer,
  getLungsRadiometer,
  getOcsStart,
  getOffOcs,
  getPerfusionTime,
} from '@features/components/ClinicalData/utils';
import { clinicalDataGroupedItems, ClinicalLogSortTypes } from '@features/components/ClinicalData/constants';
import { ClinicalBarData } from '@features/components/ClinicalBarChart';
import {
  ClinicalData,
  ClinicalDataItem,
  ClinicalDataRecord,
  ClinicalItemType,
  ClinicalMediaFile,
  OrganType,
  PfRatioData,
  RadiometerData,
  VitalParameters,
} from '@/api/apis/case';
import { getClinicalRecordByType } from '@/utils/helpers';

const pfRatioMapper = (item: PfRatioData) => item.averagePFRatio || item.pfRatio || 0;

type ClinicalMeasurement = { timestamp: string; timeZone: string };

type OcsStartTime = { timestamp: string; timezone: string };

type OffOcsTime = { timestamp: string } | null;

type MapClinicalMeasurementsParams<T extends ClinicalMeasurement, TFields extends Record<string, unknown>> = {
  clinicalItems: T[];
  fieldsMapper: (item: T) => TFields;
  ocsStart: OcsStartTime;
  offOcs?: OffOcsTime;
};

const mapClinicalMeasurementsToBarData = <T extends ClinicalMeasurement, TFields extends Record<string, unknown>>({
  clinicalItems,
  fieldsMapper,
  ocsStart,
  offOcs = null,
}: MapClinicalMeasurementsParams<T, TFields>) => {
  if (!clinicalItems) {
    return [];
  }

  const ocsStartTime = new Date(ocsStart.timestamp).getTime();
  const offOcsTime = offOcs?.timestamp ? new Date(offOcs.timestamp).getTime() : null;

  const itemsMapped = clinicalItems.map((item) => {
    const itemTime = new Date(item.timestamp).getTime();
    const isOnOcs = itemTime >= ocsStartTime && (offOcsTime === null || itemTime <= offOcsTime);

    return {
      ...fieldsMapper(item),
      time: formatClinicalTime(item.timestamp, item.timeZone),
      ocsTime: getPerfusionTime(item.timestamp, ocsStart.timestamp),
      timestamp: item.timestamp,
      isOnOcs,
    };
  });

  return orderBy(itemsMapped, 'timestamp');
};

const formatPFRatio = (clinicalData: ClinicalData): ClinicalBarData[] => {
  const ocsStart = getOcsStart(clinicalData);
  const offOcs = getOffOcs(clinicalData);
  const measurements = getLungsRadiometer(clinicalData);

  return mapClinicalMeasurementsToBarData({
    clinicalItems: measurements,
    fieldsMapper: (item) => ({ value: +pfRatioMapper(item).toFixed(2) }),
    ocsStart,
    offOcs,
  });
};

const formatBloodGases = (clinicalData: ClinicalData) => {
  const ocsStart = getOcsStart(clinicalData);
  const offOcs = getOffOcs(clinicalData);
  const measurements = getLungsRadiometer(clinicalData);

  return mapClinicalMeasurementsToBarData({
    clinicalItems: measurements,
    fieldsMapper: (item) => ({
      pfRatio: +pfRatioMapper(item).toFixed(1),
      paO2: +(item?.radiometer?.firstSection?.paO2?.toFixed(1) || 0),
      pvO2: +(item?.radiometer?.firstSection?.pvO2?.toFixed(1) || 0),
    }),
    ocsStart,
    offOcs,
  });
};

const formatLactate = (
  clinicalData: ClinicalData,
  options: { excludeNullLactate?: boolean } = {},
): ClinicalBarData[] => {
  const { excludeNullLactate = false } = options;
  const ocsStart = getOcsStart(clinicalData);
  const offOcs = getOffOcs(clinicalData);
  const measurements = getBloodSamplesRadiometer(clinicalData);
  const filteredMeasurements = excludeNullLactate
    ? measurements.filter((item) => item?.radiometer?.firstSection?.lac !== null)
    : measurements;
  const lactateMapper = (item: RadiometerData) => item?.radiometer?.firstSection?.lac || 0;
  return mapClinicalMeasurementsToBarData({
    clinicalItems: filteredMeasurements,
    fieldsMapper: (item) => ({ value: +lactateMapper(item).toFixed(2) }),
    ocsStart,
    offOcs,
  });
};

export type VitalsFormatted = {
  vitalParameters?: VitalParameters;
  measurementMinute: number;
  file?: ClinicalMediaFile;
  timestamp: string;
  timeZone: string;
};

const formatVitals = (clinicalData: ClinicalData): VitalsFormatted[] => {
  const vitals = getClinicalRecordByType(clinicalData, ClinicalItemType.VitalUpdates)?.items || [];
  return vitals.map((v) => {
    return {
      vitalParameters: v.vitalParameters,
      measurementMinute: v.vitalParameters?.readingTimeInMinutes || 0,
      file: v.mediaFiles?.[0],
      timestamp: v.timestamp,
      timeZone: v.timeZone,
    };
  });
};

const mapMediaFilesWithTimestamp = (item: ClinicalDataItem): FileWithTimestamp[] => {
  if (!item?.mediaFiles) return [];
  return item.mediaFiles.map((f) => ({
    ...f,
    timestamp: item.timestamp,
    timeZone: item.timeZone,
  }));
};

export const flatMediaFilesFromItems = (
  clinicalData: ClinicalData,
  clinicalItemType: ClinicalItemType,
): FileWithTimestamp[] => {
  const ocsVideoClinicalItem = getClinicalRecordByType(clinicalData, clinicalItemType);

  return (ocsVideoClinicalItem?.items || []).map((item: ClinicalDataItem) => mapMediaFilesWithTimestamp(item)).flat();
};

export const getVitalsGroupItems = (clinicalData: ClinicalData): VitalsFormatted[] => formatVitals(clinicalData);

const takeEarliestGroupedLogEntry = (
  clinicalData: ClinicalData,
  clinicalItemType: ClinicalItemType,
): ClinicalDataItem | null => {
  const clinicalRecords: ClinicalDataItem[] | undefined = getClinicalRecordByType(
    clinicalData,
    clinicalItemType,
  )?.items;

  if (!clinicalRecords?.length) {
    return null;
  }

  const orderedClinicalRecords: ClinicalDataItem[] = orderBy(clinicalRecords, 'timestamp', 'asc');

  return first(orderedClinicalRecords) ?? null;
};
export const groupClinicalDataItems = (
  clinicalData: ClinicalData,
  clinicalDataRecord: { clinicalItemType: ClinicalItemType },
): ClinicalItem | null => {
  const earliestGroupedLogEntry: ClinicalDataItem | null = takeEarliestGroupedLogEntry(
    clinicalData,
    clinicalDataRecord.clinicalItemType,
  );
  const clinicalDataGroupedItem = clinicalDataGroupedItems[clinicalDataRecord.clinicalItemType];

  if (!earliestGroupedLogEntry || !clinicalDataGroupedItem) {
    return null;
  }
  const sortType = clinicalDataGroupedItem.sortType === ClinicalLogSortTypes.timeForward ? 'asc' : 'desc';
  const commonGroupData: Omit<ClinicalItem, 'items' | 'mediaFiles'> = {
    id: earliestGroupedLogEntry.id,
    clinicalItemType: clinicalDataRecord.clinicalItemType,
    timestamp: earliestGroupedLogEntry.timestamp,
    timeZone: earliestGroupedLogEntry.timeZone,
  };

  switch (clinicalDataRecord.clinicalItemType) {
    case ClinicalItemType.VitalUpdates:
      return {
        ...commonGroupData,
        items: orderBy(getVitalsGroupItems(clinicalData), 'measurementMinute', sortType),
      } as ClinicalItem;

    default:
      return null;
  }
};

const formatOCSMonitorData = (clinicalData: ClinicalData) => {
  const ocsStart = getOcsStart(clinicalData);
  const oscMonitorData = getClinicalRecordByType(clinicalData, ClinicalItemType.Stabilization)?.items || [];
  return oscMonitorData.flatMap((c) => {
    if (!c?.mediaFiles) return [];
    return c.mediaFiles?.map((f) => ({
      file: f,
      timestamp: c.timestamp,
      timeZone: c.timeZone,
      ocsTime: getPerfusionTime(c.timestamp, ocsStart.timestamp),
    }));
  });
};

const formatRadiometerData = (clinicalData: ClinicalData, organType: OrganType) => {
  const ocsStart = getOcsStart(clinicalData);
  const data =
    organType === OrganType.Lungs ? getLungsRadiometer(clinicalData) : getBloodSamplesRadiometer(clinicalData);
  return data.map(({ radiometer, mediaFiles, timestamp, timeZone, radiometerType }) => {
    const { bEecf, sO2, hcO3, paO2, pH, pcO2, tcO2 } = radiometer?.firstSection ?? {};
    return {
      stats: { bEecf, sO2, hcO3, paO2, pH, pcO2, tcO2, type: radiometerType },
      mediaFiles,
      timeZone,
      timestamp,
      ocsTime: getPerfusionTime(timestamp, ocsStart.timestamp),
    };
  });
};

const getClinicalItemTimestamp = (clinicalDataRecord?: ClinicalDataRecord) => {
  const firstItem = clinicalDataRecord?.item || (clinicalDataRecord?.items || [])[0];
  const { timeZone, timestamp } = firstItem || {};

  if (!timeZone || !timestamp) {
    return null;
  }

  return {
    timeZone,
    timestamp,
  };
};

const formatBile = (clinicalData: ClinicalData) => {
  const bileRecord = getClinicalRecordByType(clinicalData, ClinicalItemType.Bile);
  return (
    bileRecord?.items?.map((item) => ({
      value: item.yesNo,
      timestamp: item.timestamp,
      timeZone: item.timeZone,
      details: item.details,
    })) ?? []
  );
};

export {
  formatBloodGases,
  formatPFRatio,
  formatLactate,
  formatVitals,
  formatOCSMonitorData,
  formatRadiometerData,
  getClinicalItemTimestamp,
  mapMediaFilesWithTimestamp,
  formatBile,
};
